// <nav-drawer> — the mobile navigation drawer. A native <dialog> does the
// heavy lifting (modality, Escape, backdrop, focus containment); this
// element just wires the triggers. Markup convention (rendered by
// Components::Shell::Sidebar):
//
//   <nav-drawer>
//     <header>… <button data-drawer-open>☰</button></header>
//     <dialog data-drawer-dialog>
//       <button data-drawer-close>✕</button>
//       <nav>…links…</nav>
//     </dialog>
//   </nav-drawer>
//
// Behaviour: open button → showModal; close button, backdrop click (clicks
// landing on the dialog element itself are outside the content box), and
// following any link all close it. Torn down before Turbo caches the page
// so restored snapshots never carry an open drawer.
//
// Without JS the drawer can't open — the desktop sidebar still renders from
// lg up, so this degrades to "rotate or widen the window", which is the
// accepted trade for keeping the markup dependency-free.

class NavDrawer extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		const dialog = this.querySelector<HTMLDialogElement>(
			"[data-drawer-dialog]",
		);
		if (!dialog) return;

		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.querySelector("[data-drawer-open]")?.addEventListener(
			"click",
			() => dialog.showModal(),
			{ signal },
		);

		this.querySelector("[data-drawer-close]")?.addEventListener(
			"click",
			() => dialog.close(),
			{ signal },
		);

		dialog.addEventListener(
			"click",
			(event) => {
				const target = event.target as Element | null;
				if (event.target === dialog || target?.closest("a")) {
					dialog.close();
				}
			},
			{ signal },
		);

		document.addEventListener("turbo:before-cache", () => dialog.close(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}
}

if (!customElements.get("nav-drawer")) {
	customElements.define("nav-drawer", NavDrawer);
}
